import { getEnv } from "@/config/env";
import { useAuth } from "@/contexts/AuthContext";
import { processFileName } from "@/lib/fileUtils";
import { useMutation } from "@tanstack/react-query";

const DATA_INGESTION_API_URL =
  getEnv("VITE_DATA_INGESTION_API_URL") || undefined;

export interface UploadedFile {
  filename: string;
  download_url: string;
  status: string;
}

export type UploadFilesResponse = UploadedFile[];

async function uploadFiles(
  files: File[],
  getAccessToken: () => Promise<string | null>
): Promise<UploadFilesResponse> {
  const token = await getAccessToken();
  if (!token) throw new Error("Not authenticated");

  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file, processFileName(file.name));
  });

  const response = await fetch(`${DATA_INGESTION_API_URL}/api/ingestion/upload-files`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (response.status === 401) {
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || `Upload failed: ${response.status}`);
  }

  const results: UploadFilesResponse = await response.json();
  return results.map((file) => ({
    ...file,
    download_url: file.download_url.startsWith("/blob")
      ? `${DATA_INGESTION_API_URL}${file.download_url}`
      : file.download_url,
  }));
}

export function useFileUpload() {
  const { getAccessToken } = useAuth();

  return useMutation({
    mutationFn: (files: File[]) => uploadFiles(files, getAccessToken),
  });
}
